/**
 * 本から TODO を作るときに、どのフォーマットで作るかを選ぶシート。
 *
 * フォーマットが 2 つ以上あるときだけ本カードのボタンから開く（1 つならすぐ追加する）。
 * 同じ本・同じタイトルの未完了 TODO があるフォーマットは「追加済み」にして押せなくする。
 */

import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { CalilBook } from '@/api/types/calil'
import { Badge } from '@/components/ui/badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { useBookTodoTemplateStore } from '@/store/useBookTodoTemplateStore'

import { addBookTodoWithFormat } from '../lib/book-todo-actions'
import { type BookTodoFormat, effectiveBookTodoFormats, renderBookTodoTitle } from '../lib/book-todo-format'

type Props = {
  book: CalilBook
  /** 正規化済みの ISBN。無ければ本へのリンクと「追加済み」の判定をしない */
  isbn: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const NO_TITLES: string[] = []

export const BookTodoSheet = ({ book, isbn, open, onOpenChange }: Props) => {
  const templates = useBookTodoTemplateStore((s) => s.templates)
  const isLoaded = useBookTodoTemplateStore((s) => s.isLoaded)
  const existingTitles = useBookTodoTemplateStore((s) => (isbn ? s.existingTitles.get(isbn) : undefined)) ?? NO_TITLES
  const loadTemplates = useBookTodoTemplateStore((s) => s.loadTemplates)
  const loadExistingTitles = useBookTodoTemplateStore((s) => s.loadExistingTitles)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (!open) return
    loadTemplates().catch(() => toast.error('フォーマットの読み込みに失敗しました'))
    if (isbn) loadExistingTitles(isbn).catch(() => toast.error('作成済みの TODO を確認できませんでした'))
  }, [open, isbn, loadTemplates, loadExistingTitles])

  const fields = useMemo(
    () => ({ title: book.title, author: book.author, publisher: book.publisher, isbn }),
    [book.title, book.author, book.publisher, isbn],
  )
  const formats = useMemo(() => effectiveBookTodoFormats(templates), [templates])

  const add = async (format: BookTodoFormat) => {
    setPending(true)
    const added = await addBookTodoWithFormat(format, fields)
    setPending(false)
    if (added) onOpenChange(false)
  }

  return (
    <BottomSheet title="タスクに追加" open={open} onOpenChange={onOpenChange} snapPoints={[0.6]}>
      <div className="space-y-4 px-4 pb-6">
        <p className="line-clamp-2 text-xs text-muted-foreground">{book.title}</p>
        <div className="flex flex-col gap-2">
          {isLoaded &&
            formats.map((f) => {
              const title = renderBookTodoTitle(f.format, fields)
              const added = existingTitles.includes(title)
              return (
                <Button
                  key={f.id ?? 'default'}
                  type="button"
                  variant="outline"
                  disabled={pending || added || !title}
                  onClick={() => void add(f)}
                  className="h-auto justify-between gap-2 py-2 text-left whitespace-normal"
                >
                  <span className="wrap-break-word">{title}</span>
                  {f.project && <Badge variant="secondary">{f.project}</Badge>}
                  {added && <span className="shrink-0 text-xs text-emerald-400">追加済み</span>}
                </Button>
              )
            })}
        </div>
      </div>
    </BottomSheet>
  )
}
